import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus, Trash2, Pencil } from 'lucide-react';
import type { SponsorInput, SponsorWithStats } from '@dmn/shared';
import { RowAction, RowActions } from '@/components/RowActions';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { ImageDropzone } from '@/components/ImageDropzone';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TablePagination } from '@/components/TablePagination';
import { usePagination } from '@/hooks/use-pagination';
import { TableSearch } from '@/components/TableSearch';
import { useTableSearch } from '@/hooks/use-table-search';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queries';

export const Route = createFileRoute('/sponsors/')({ component: SponsorsIndex });

/** Reserve the final id before save so a new sponsor's logo has a real owner path. */
const newSponsorId = (): string => `spo_${crypto.randomUUID().replaceAll('-', '').slice(0, 16)}`;

function SponsorsIndex() {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<SponsorWithStats | null>(null);
  const [creatingId, setCreatingId] = useState<string | null>(null);

  const sponsors = useQuery({ queryKey: queryKeys.sponsors, queryFn: api.sponsors.list });

  const save = useMutation({
    mutationFn: ({
      id,
      input,
      creating,
    }: {
      id: string;
      input: SponsorInput;
      creating: boolean;
    }) => (creating ? api.sponsors.create({ id, ...input }) : api.sponsors.update(id, input)),
    onSuccess: async () => {
      setCreatingId(null);
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.sponsors });
    },
  });

  const search = useTableSearch(sponsors.data, (s) => [
    s.name,
    s.legalName,
    s.id,
    s.contactEmail,
    s.status,
  ]);
  const pagination = usePagination(search.filtered);
  const removeSponsor = useMutation({
    mutationFn: (id: string) => api.sponsors.remove(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.sponsors });
    },
  });

  return (
    <>
      <PageHeader
        title="Sponsors"
        description="Brands and their counsel-approved safety language"
        actions={
          <Button size="sm" onClick={() => setCreatingId(newSponsorId())}>
            <Plus className="size-4" />
            New sponsor
          </Button>
        }
      />
      <PageBody>
        <TableSearch
          search={search}
          placeholder="Search name, legal name, id or contact"
          label="Search sponsors"
          noun="sponsor"
        />
        <Card className="shadow-none">
          <CardContent className="p-0">
            {sponsors.isError ? (
              <ErrorState error={sponsors.error} onRetry={() => void sponsors.refetch()} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sponsor</TableHead>
                    <TableHead>Legal entity</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Campaigns</TableHead>
                    <TableHead className="text-right">Approved language</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sponsors.isPending && <LoadingRows columns={6} />}
                  {pagination.pageRows.map((sponsor) => (
                    <TableRow key={sponsor.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {sponsor.brandColor && (
                            <span
                              className="size-3 shrink-0 rounded-full border"
                              style={{ background: sponsor.brandColor }}
                            />
                          )}
                          <span className="font-medium">{sponsor.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{sponsor.legalName}</TableCell>
                      <TableCell>
                        <Badge variant={sponsor.status === 'active' ? 'default' : 'secondary'}>
                          {sponsor.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {sponsor.activeCampaignCount}/{sponsor.campaignCount}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {sponsor.approvedSafetyText.length}
                      </TableCell>
                      <TableCell className="w-px text-right">
                        <RowActions>
                          <RowAction
                            icon={Pencil}
                            label={`Edit ${sponsor.name}`}
                            onClick={() => setEditing(sponsor)}
                          />
                          <RowAction
                            icon={Trash2}
                            label={`Delete ${sponsor.name}`}
                            destructive
                            disabled={removeSponsor.isPending}
                            onClick={() => {
                              if (
                                window.confirm(`Delete ${sponsor.name}? This cannot be undone.`)
                              ) {
                                removeSponsor.mutate(sponsor.id);
                              }
                            }}
                          />
                        </RowActions>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <TablePagination pagination={pagination} label="sponsors" />
            {sponsors.data?.length === 0 && (
              <EmptyState
                title="No sponsors"
                description="A campaign needs a sponsor before it can be created."
                icon={Building2}
                action={
                  <Button size="sm" onClick={() => setCreatingId(newSponsorId())}>
                    New sponsor
                  </Button>
                }
              />
            )}
          </CardContent>
        </Card>
        {removeSponsor.isError && (
          <p className="text-sm text-destructive">
            {removeSponsor.error instanceof ApiError
              ? removeSponsor.error.message
              : 'Could not delete'}
          </p>
        )}
      </PageBody>

      <SponsorDialog
        open={creatingId !== null || editing !== null}
        sponsor={editing}
        ownerId={editing?.id ?? creatingId}
        pending={save.isPending}
        error={save.error}
        onSave={(input) => {
          const id = editing?.id ?? creatingId;
          if (id) save.mutate({ id, input, creating: editing === null });
        }}
        onOpenChange={(open) => {
          if (!open) {
            setCreatingId(null);
            setEditing(null);
            save.reset();
          }
        }}
      />
    </>
  );
}

function SponsorDialog({
  open,
  sponsor,
  ownerId,
  pending,
  error,
  onSave,
  onOpenChange,
}: {
  open: boolean;
  sponsor: SponsorWithStats | null;
  ownerId: string | null;
  pending: boolean;
  error: unknown;
  onSave: (input: SponsorInput) => void;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [brandColor, setBrandColor] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [logoAssetPath, setLogoAssetPath] = useState<string | null>(null);
  const [safetyText, setSafetyText] = useState('');

  // Reset when the dialog opens so a cancelled edit does not leak into the next.
  const [lastOpenedFor, setLastOpenedFor] = useState<string | null>(null);
  const key = sponsor?.id ?? 'new';
  if (open && lastOpenedFor !== key) {
    setLastOpenedFor(key);
    setName(sponsor?.name ?? '');
    setLegalName(sponsor?.legalName ?? '');
    setBrandColor(sponsor?.brandColor ?? '');
    setContactEmail(sponsor?.contactEmail ?? '');
    setLogoAssetPath(sponsor?.logoAssetPath ?? null);
    setSafetyText((sponsor?.approvedSafetyText ?? []).join('\n\n'));
  }
  if (!open && lastOpenedFor !== null) setLastOpenedFor(null);

  const valid = name.trim() && legalName.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{sponsor ? 'Edit sponsor' : 'New sponsor'}</DialogTitle>
          <DialogDescription>
            Approved language is the only wording a creative's safety text may draw from.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2.5">
              <Label htmlFor="sponsor-name">Display name</Label>
              <Input
                id="sponsor-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className="grid gap-2.5">
              <Label htmlFor="legal-name">Legal entity</Label>
              <Input
                id="legal-name"
                value={legalName}
                onChange={(event) => setLegalName(event.target.value)}
              />
            </div>
            <div className="grid gap-2.5">
              <Label htmlFor="brand-color">Brand colour</Label>
              <div className="flex gap-2">
                <Input
                  id="brand-color"
                  value={brandColor}
                  placeholder="#0f766e"
                  className="font-mono"
                  onChange={(event) => setBrandColor(event.target.value)}
                />
                <Input
                  type="color"
                  aria-label="Choose brand colour"
                  value={/^#[0-9a-fA-F]{6}$/.test(brandColor) ? brandColor : '#0f766e'}
                  className="w-11 shrink-0 cursor-pointer p-1"
                  onChange={(event) => setBrandColor(event.target.value)}
                />
              </div>
            </div>
          </div>

          <ImageDropzone
            className="space-y-2.5"
            label="Logo"
            scope="sponsors"
            ownerId={ownerId ?? 'unavailable'}
            value={logoAssetPath}
            onChange={setLogoAssetPath}
            hint="Shown in the console and in sponsor reporting. PNG, JPEG or WebP."
            disabled={!ownerId || pending}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2.5">
              <Label htmlFor="contact">Contact email</Label>
              <Input
                id="contact"
                type="email"
                value={contactEmail}
                onChange={(event) => setContactEmail(event.target.value)}
              />
            </div>
          </div>
          <div className="grid gap-2.5">
            <Label htmlFor="safety">Approved safety language</Label>
            <Textarea
              id="safety"
              rows={5}
              value={safetyText}
              onChange={(event) => setSafetyText(event.target.value)}
              placeholder="One statement per paragraph — separate with a blank line."
            />
          </div>

          {sponsor && <SponsorUsersCard sponsorId={sponsor.id} sponsorName={sponsor.name} />}

          {error != null && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error instanceof ApiError ? error.message : 'Could not save the sponsor'}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!valid || pending}
            onClick={() =>
              onSave({
                name: name.trim(),
                legalName: legalName.trim(),
                logoAssetPath,
                brandColor: /^#[0-9a-fA-F]{6}$/.test(brandColor) ? brandColor : null,
                contactEmail: contactEmail.trim() || null,
                approvedSafetyText: safetyText
                  .split(/\n{2,}/)
                  .map((entry) => entry.trim())
                  .filter(Boolean),
                status: sponsor?.status ?? 'active',
              })
            }
          >
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Sponsor portal access — read-only reporting for this sponsor's own contacts
 * (§4, CLAUDE.md).
 *
 * Only rendered while editing an existing sponsor: a brand-new one is not yet
 * a document in Firestore, and the invite endpoint 404s until it is.
 *
 * **Inviting grants access and sends nothing.** The sponsor asks for their own
 * link at `/portal/signin`, where Firebase mails it to them. So an operator
 * never handles a credential, there is no link to mislay or forward on, and a
 * sponsor returning months later needs nobody's help — they repeat the same
 * step they used the first time.
 */
function SponsorUsersCard({ sponsorId, sponsorName }: { sponsorId: string; sponsorName: string }) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const users = useQuery({
    queryKey: queryKeys.sponsorUsers(sponsorId),
    queryFn: () => api.sponsors.users.list(sponsorId),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.sponsorUsers(sponsorId) });

  const invite = useMutation({
    mutationFn: async (address: string) => {
      await api.sponsors.users.invite(sponsorId, { email: address });
      setMessage({
        tone: 'success',
        text: `${address} now has access. They sign in at /portal/signin, where Firebase emails them a link.`,
      });
    },
    onSuccess: async () => {
      setEmail('');
      await invalidate();
    },
    onError: (caught) => {
      setMessage({
        tone: 'error',
        text: caught instanceof ApiError ? caught.message : 'Could not invite that address',
      });
    },
  });

  const revoke = useMutation({
    mutationFn: (uid: string) => api.sponsors.users.revoke(sponsorId, uid),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-3 rounded-md border p-3">
      <p className="text-sm font-medium">Sponsor portal access</p>
      <p className="text-xs text-muted-foreground">
        Read-only reporting for this sponsor&rsquo;s own contacts. Inviting only grants access —
        they sign themselves in at <code className="font-mono">/portal/signin</code>, where Firebase
        emails them a link.
      </p>
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          if (email.trim()) invite.mutate(email.trim());
        }}
      >
        <Input
          type="email"
          placeholder="contact@sponsor.example"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Button type="submit" size="sm" disabled={invite.isPending || !email.trim()}>
          Invite
        </Button>
      </form>
      {message && (
        <p
          className={
            message.tone === 'error' ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'
          }
        >
          {message.text}
        </p>
      )}
      <div className="divide-y">
        {(users.data ?? []).map((user) => (
          <div key={user.uid} className="flex items-center justify-between gap-2 py-2 text-sm">
            <div className="min-w-0">
              <p className="truncate">{user.email}</p>
              {user.pendingInvite && (
                <p className="text-xs text-muted-foreground">Invited, not yet signed in</p>
              )}
            </div>
            <div className="flex shrink-0 gap-1">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  if (window.confirm(`Revoke ${sponsorName} access for ${user.email}?`)) {
                    revoke.mutate(user.uid);
                  }
                }}
                disabled={revoke.isPending}
              >
                Revoke
              </Button>
            </div>
          </div>
        ))}
        {users.isPending && <p className="py-2 text-sm text-muted-foreground">Loading…</p>}
        {users.data?.length === 0 && (
          <p className="py-2 text-sm text-muted-foreground">No sponsor users invited yet.</p>
        )}
      </div>
    </div>
  );
}
